import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/joy";
import { Button } from "@mui/material";
import { AddRounded } from "@mui-symbols-material/w300";
import { FilteredSearch, LoadingScreen } from "@dreebit-registry/vsm-microservice-components";
import { InstalledBaseIotReportSubscriptions } from "../../graphql/queries/installedBaseIotReportSubscriptions";
import IotReportSubscriptionModal from "../IotReportSubscriptionModal";
import NotificationSubscriptionCard from "../NotificationSubscriptionCard";
import DeleteIotReportSubscriptionModal from "../DeleteIotReportSubscriptionModal";
import NotificationSubscriptionsEmptyIllustration from "../NotificationSubscriptionsEmptyIllustration";

interface ReportSubscriptionRow {
    id: string;
    inApp: boolean;
    mail: boolean;
    push: boolean;
    clientId: string;
    clientName: string;
    insert?: string | null;
    update?: string | null;
}

const CREATE_BUTTON_SX = {
    color: "#333333",
    backgroundColor: "#FDDDCD",
    "&:hover": { backgroundColor: "#FFBA99" },
};

const IotReportSubscriptionsList = () => {
    const { t } = useTranslation();

    const { data, loading } = useQuery(InstalledBaseIotReportSubscriptions, {
        fetchPolicy: "cache-and-network",
    });

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editClientId, setEditClientId] = useState<string | undefined>(undefined);
    const [deleteRow, setDeleteRow] = useState<ReportSubscriptionRow | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");

    const rows: ReportSubscriptionRow[] = useMemo(() => {
        const subs = (data?.installedBaseIotReportSubscriptionList?.subscriptions ?? []) as any[];

        return subs
            .filter((sub) => !!sub?.client?.id)
            .map((sub) => ({
                id: sub.id,
                inApp: !!sub.inApp,
                mail: !!sub.mail,
                push: !!sub.push,
                clientId: sub.client.id,
                clientName: sub.client.name ?? "",
                insert: sub.insert,
                update: sub.update,
            }));
    }, [data]);

    const filteredRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => r.clientName.toLowerCase().includes(q));
    }, [rows, searchQuery]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 }}>
            <div
                className={"flex-row"}
                style={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 8px 13px 8px",
                    gap: 12,
                }}
            >
                <FilteredSearch
                    value={searchQuery}
                    defaultFields={[]}
                    onSearch={(v: any) => setSearchQuery(v?.query ?? "")}
                />
                <Button
                    variant="contained"
                    startIcon={<AddRounded />}
                    onClick={() => setAddModalOpen(true)}
                    sx={CREATE_BUTTON_SX}
                >
                    {t("Add")}
                </Button>
            </div>

            {loading && rows.length === 0 ? (
                <LoadingScreen />
            ) : filteredRows.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: "48px" }}>
                    <NotificationSubscriptionsEmptyIllustration
                        variant={searchQuery ? "search" : "bell"}
                        width={200}
                    />
                    <Typography level="body-md" sx={{ pt: "16px", textAlign: "center", color: "text.secondary" }}>
                        {searchQuery
                            ? t("No subscriptions match your search.")
                            : t("You have no IoT report subscriptions yet.")}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ overflowY: "auto", flex: 1, px: "8px", pb: "8px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {filteredRows.map((row) => (
                            <NotificationSubscriptionCard
                                key={row.id}
                                subscription={{
                                    id: row.id,
                                    title: row.clientName,
                                    breadcrumb: "",
                                    inApp: row.inApp,
                                    mail: row.mail,
                                    push: row.push,
                                    iotAlert: false,
                                    insert: row.insert,
                                    update: row.update,
                                }}
                                onEdit={() => setEditClientId(row.clientId)}
                                onDelete={() => setDeleteRow(row)}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {addModalOpen && (
                <IotReportSubscriptionModal
                    open={addModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    allowClientPicker
                />
            )}
            {editClientId && (
                <IotReportSubscriptionModal
                    open={!!editClientId}
                    onClose={() => setEditClientId(undefined)}
                    clientId={editClientId}
                />
            )}
            {deleteRow && (
                <DeleteIotReportSubscriptionModal
                    open={Boolean(deleteRow)}
                    subscriptionId={deleteRow.id}
                    clientName={deleteRow.clientName}
                    onClose={() => setDeleteRow(null)}
                />
            )}
        </Box>
    );
};

export default IotReportSubscriptionsList;
